import { useTranslation } from '@govard/i18n-core/react';
import { Alert, Card, Skeleton, Tag, Timeline, Typography } from 'antd';
import type { ReactElement } from 'react';

import type { AiChatEventEnvelope } from '@govard/ai-chat-kit';

export interface AgentTurnPanelProps {
  conversationId: string;
  turnId: string;
  status: string;
  replay: AiChatEventEnvelope[];
  loading?: boolean;
  error?: string;
}

function extractTimelineText(event: AiChatEventEnvelope): string {
  const type = event.type;
  if (type === 'assistant_delta') {
    const payload = event.payload as { content?: string; text?: string };
    const text = payload.content ?? payload.text ?? '';
    return text.length > 0 ? text : type;
  }
  if (type === 'tool_call') {
    const payload = event.payload as { call?: { name?: string }; toolName?: string };
    const name = payload.call?.name ?? payload.toolName ?? '';
    return name.length > 0 ? name : type;
  }
  const fallback = event.payload as { content?: string; text?: string };
  const maybe = fallback.content ?? fallback.text;
  return typeof maybe === 'string' && maybe.length > 0 ? maybe : type;
}

export function AgentTurnPanel(props: AgentTurnPanelProps): ReactElement {
  const { conversationId, turnId, status, replay, loading, error } = props;
  const { t } = useTranslation();

  if (loading === true) {
    return <Skeleton active paragraph={{ rows: 4 }} />;
  }

  if (typeof error === 'string' && error.length > 0) {
    return <Alert type="error" showIcon title={error} />;
  }

  const items = replay.map((event) => ({
    children: extractTimelineText(event),
  }));

  return (
    <Card
      title={t('crm.telegram_candidates.agent_turn.panel.title' as never)}
      extra={<Tag>{status}</Tag>}
    >
      <Timeline items={items} />
      <Typography.Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
        {conversationId} · {turnId}
      </Typography.Text>
    </Card>
  );
}
